import MUISelectInput from '@brands/Components/Inputs/MuiSelectInput/MUISelectInput';
import PhoneInput from '@brands/Components/Inputs/MuiTelInput/MuiTelInput';
import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import ProgressBar from '@brands/Components/ProgressBar/ProgressBar';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { stateOptions } from '@brands/Utils/selectOptions';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@mui/material';
import { E164Number, isValidPhoneNumber } from 'libphonenumber-js';
import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';

import styles from './styles.module.scss';
import { PharmacySubmit } from './types';
import { validationSchema } from './validationSchema';

interface AddPharmacyProps {
  setFindPharmacy: React.Dispatch<React.SetStateAction<boolean>>;
  setAddPharmacy: React.Dispatch<React.SetStateAction<boolean>>;
  AddNewPharmacy: (newPharmacy: PharmacySubmit) => Promise<void>;
  isFromPatientIntake?: boolean;
}

const AddPharmacy: React.FC<AddPharmacyProps> = ({
  setFindPharmacy,
  setAddPharmacy,
  AddNewPharmacy,
  isFromPatientIntake = false,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
  } = useForm<PharmacySubmit>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  });
  const phoneInputRef = useRef<HTMLInputElement>(null);

  const onInvalid = (err: any): void => {
    const error = Object.values(err)[0] as any;
    if (error) {
      displayErrorDetails(error);
    }
  };

  const onSubmitProfile = (date: PharmacySubmit): void => {
    AddNewPharmacy(date);
  };

  const watchedFields = watch([
    'pharmacy_name',
    'pharmacy_street_address',
    'pharmacy_city',
    'pharmacy_state',
    'pharmacy_zip_code',
    'pharmacy_phone_number',
  ]);

  useEffect(() => {
    const [name, street, city, state, zip, phone] = watchedFields;

    const allFilled =
      !!name?.trim() && !!street?.trim() && !!city?.trim() && !!state?.trim() && !!zip?.trim() && !!phone?.trim();

    const isPhoneValid = isValidPhoneNumber(phone || '');

    if (isFromPatientIntake && allFilled && isPhoneValid) {
      const newPharmacy: PharmacySubmit = {
        pharmacy_name: name,
        pharmacy_street_address: street,
        pharmacy_city: city,
        pharmacy_state: state,
        pharmacy_zip_code: zip,
        pharmacy_phone_number: phone,
      };

      AddNewPharmacy(newPharmacy);
    }
  }, [watchedFields, isFromPatientIntake]);

  return (
    <div className={`${styles.pharmacyContainer} ${isFromPatientIntake && styles.pharmacyContainerPatientIntake}`}>
      {!isFromPatientIntake && (
        <ProgressBar
          currentStep={2}
          totalSteps={4}
          onBack={() => {
            setAddPharmacy(false);
            setFindPharmacy(true);
          }}
          hideStepNumbers
        />
      )}
      <h2 className={`fs-unmask ${isFromPatientIntake && styles.patientIntakeh2}`}>Add pharmacy information</h2>
      <div className={styles.findPharmacy}>
        <form
          className={`${isFromPatientIntake && styles.addPharmacyForm}`}
          onSubmit={handleSubmit(onSubmitProfile, onInvalid)}
        >
          <div className={styles.inputWrap}>
            <TextField
              {...register('pharmacy_name')}
              required
              data-testid="add-pharmacy-name"
              label="Pharmacy name"
              errors={errors.pharmacy_name ? errors : {}}
              name="pharmacy_name"
              onChange={(e: any) => {
                if (errors.pharmacy_name) clearErrors('pharmacy_name');
                setValue('pharmacy_name', e.target.value);
              }}
              inputClass={`${styles.profileInputClass} fs-exclude`}
              wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
            />
          </div>
          <div className={styles.inputWrap}>
            <TextField
              {...register('pharmacy_street_address')}
              label="Pharmacy street address"
              data-testid="add-pharmacy-address"
              errors={errors.pharmacy_street_address ? errors : {}}
              name="pharmacy_street_address"
              onChange={(e: any) => {
                if (errors.pharmacy_street_address) clearErrors('pharmacy_street_address');
                setValue('pharmacy_street_address', e.target.value);
              }}
              required
              inputClass={`${styles.profileInputClass} fs-exclude`}
              wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
            />
          </div>
          <div className={styles.row}>
            <div className={styles.inputWrap}>
              <TextField
                {...register('pharmacy_city')}
                label="Pharmacy city"
                data-testid="add-pharmacy-city"
                errors={errors.pharmacy_city ? errors : {}}
                name="pharmacy_city"
                onChange={(e: any) => {
                  if (errors.pharmacy_city) clearErrors('pharmacy_city');
                  setValue('pharmacy_city', e.target.value);
                }}
                required
                inputClass={`${styles.profileInputClass} fs-exclude`}
                wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
              />
            </div>
            <div className={styles.inputWrap}>
              <MUISelectInput
                {...register('pharmacy_state')}
                label="Pharmacy state"
                data-testid="add-pharmacy-state"
                errors={errors.pharmacy_state ? errors : {}}
                name="pharmacy_state"
                onChange={(e: any) => {
                  if (errors.pharmacy_state) clearErrors('pharmacy_state');
                  setValue('pharmacy_state', e);
                }}
                value={watch('pharmacy_state') || ''}
                required
                inputClass={`${styles.profileInputClass} fs-exclude`}
                wrapperClassName={`fs-unmask ${styles.completeProafileClass}`}
                options={stateOptions}
              />
            </div>
            <div className={styles.inputWrap}>
              <TextField
                {...register('pharmacy_zip_code')}
                label="Pharmacy zip code"
                data-testid="add-pharmacy-zipcode"
                errors={errors.pharmacy_zip_code ? errors : {}}
                name="pharmacy_zip_code"
                onChange={(e: any) => {
                  if (errors.pharmacy_zip_code) clearErrors('pharmacy_zip_code');
                  setValue('pharmacy_zip_code', e.target.value, { shouldValidate: true });
                }}
                required
                inputClass={`${styles.profileInputClass} fs-exclude`}
                wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
              />
            </div>
          </div>
          <div className={styles.inputWrap}>
            <PhoneInput
              {...register('pharmacy_phone_number')}
              value={watch('pharmacy_phone_number') as E164Number}
              id="phone_number"
              data-testid="add-pharmacy-phone-number"
              name="pharmacy_phone_number"
              onChange={(val: any) => {
                if (val === undefined || val === '') {
                  if (errors.pharmacy_phone_number) clearErrors('pharmacy_phone_number');
                  setValue('pharmacy_phone_number', '', { shouldValidate: true });
                  return;
                }

                const formattedVal = val.startsWith('+1') ? val : `+1${val.replace(/\D/g, '')}`;
                if (errors.pharmacy_phone_number) clearErrors('pharmacy_phone_number');
                setValue('pharmacy_phone_number', formattedVal, { shouldValidate: true });
              }}
              required
              label="Pharmacy phone number"
              errors={errors}
              inputClass={`${styles.profileInputClass} fs-exclude`}
              wrapperClassName={`fs-unmask ${styles.completeProfilePhone}`}
              ref={phoneInputRef}
            />
          </div>
          {!isFromPatientIntake && (
            <Button
              variant="contained"
              type="submit"
              color="primary"
              className={styles.save}
              data-testid="add-pharmacy-save-button"
            >
              Save Changes
            </Button>
          )}
        </form>
      </div>
    </div>
  );
};

export default AddPharmacy;
